import React, { useState } from 'react';

import { startCase } from 'lodash';

import { Panel } from '@shared/components/bootstrap';
import { Picture } from '@shared/components/helpers';
import { Lightbox } from '@shared/components/helpers/lightbox';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { ItemBrick } from '@admin/components/items/item_brick';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { Spinner } from '@admin/components/spinner';
import { accountURL, palletURL } from '@admin/config/routes';

import { TransferPoolPallet } from './types';

const DENSITY_TYPES = new Set(['Single', 'Double', 'Stackable Crate']);

/** JSON may encode BigDecimal percentage as a string (`encode_big_decimal_as_string`). */
const parseDensityPercentage = (raw: unknown): number | undefined => {
  if (raw == null) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const gradeLabelClass = (score: string): string => {
  switch (score.toLowerCase()) {
    case 'good':
      return 'label-success';
    case 'acceptable':
      return 'label-warning';
    case 'fail':
      return 'label-danger';
    default:
      return 'label-default';
  }
};

const LocationLink: React.FC<{ location: NonNullable<TransferPoolPallet['location']> }> = ({ location }) => (
  <a href={`/locations/${location.id}`} className="text-primary">
    #{location.value}
  </a>
);

const PalletCard: React.FC<{
  pallet: TransferPoolPallet;
}> = ({ pallet }) => {
  const [gradeLightboxUrl, setGradeLightboxUrl] = useState<string | undefined>();
  const showHeightLabel = pallet.size !== 'oversized' && Boolean(pallet.height);
  const showAccounts = Boolean(pallet.location) || pallet.state === 'enroute';
  const densityPercentage = parseDensityPercentage(pallet.density_metrics?.percentage);
  const showDensity = DENSITY_TYPES.has(pallet.type.name) && densityPercentage !== undefined;

  if (pallet.items_count <= 0) {
    return null;
  }

  return (
    <div>
      {gradeLightboxUrl && <Lightbox imageURL={gradeLightboxUrl} onClose={() => setGradeLightboxUrl(undefined)} />}

      <Panel>
        <Panel.Body>
          <div className="pull-left">
            {pallet.grade?.image && (
              <div className="pull-left panel-pallet-image">
                <button
                  type="button"
                  className="btn-link"
                  style={{ padding: 0, border: 'none', background: 'none' }}
                  aria-label="View pallet grade image"
                  onClick={() => setGradeLightboxUrl(pallet.grade?.image?.source)}
                >
                  <figure>
                    <Picture
                      source={pallet.grade.image.source}
                      alt=""
                      width={50}
                      height={50}
                      fit={ImgixFit.Fill}
                      background={ImgixBG.White}
                    />
                  </figure>
                </button>
              </div>
            )}
            <div className="pull-right">
              <a className="btn-link" href={palletURL(pallet.id)}>
                #{pallet.barcode.value}
              </a>{' '}
              <strong>({startCase(pallet.state)})</strong>{' '}
              {pallet.grade?.score && (
                <span className={`label ${gradeLabelClass(pallet.grade.score)}`}>
                  {startCase(pallet.grade.score.toLowerCase())}
                </span>
              )}
              <QaQrCodeLink value={pallet.barcode.value} />
              <br />
              <span>
                {pallet.type.name} ({pallet.items_count} {pallet.items_count === 1 ? 'Item' : 'Items'})
              </span>{' '}
              {showDensity && (
                <span className={`label ${densityPercentage >= 75 ? 'label-success' : 'label-danger'}`}>
                  Density: {densityPercentage.toFixed(2)}%
                </span>
              )}{' '}
              {showHeightLabel && pallet.height && (
                <span className="label label-default">Height: {pallet.height.value} in.</span>
              )}
            </div>
          </div>
          <div className="pull-right">
            {showAccounts && pallet.primaryCustomers && pallet.primaryCustomers.length > 0 && (
              <div>
                <strong>{pallet.primaryCustomers.length === 1 ? 'Account:' : 'Accounts:'}</strong>{' '}
                {pallet.primaryCustomers.map((customer, index) => (
                  <span key={customer.id}>
                    {index > 0 && ', '}
                    <a className="btn-link" href={accountURL({ id: customer.account_id })}>
                      {customer.name}
                    </a>
                  </span>
                ))}
              </div>
            )}
            {pallet.location ? (
              <>
                <div>
                  <strong>{startCase(pallet.location.located.type)}:</strong>{' '}
                  <span>{pallet.location.located.object.name}</span>
                </div>
                <div>
                  <strong>Location:</strong> <LocationLink location={pallet.location} />{' '}
                  <QaQrCodeLink value={pallet.location.value} />
                </div>
              </>
            ) : (
              <div>
                <strong>Location:</strong> <span>On Transfer Truck</span>
              </div>
            )}
          </div>
        </Panel.Body>
      </Panel>

      <div className="grid">
        <div className="row">
          {pallet.items.map((item) => (
            <div key={item.id} className="entry col-xs-6 col-sm-4 col-md-3 col-lg-2">
              <ItemBrick item={{ ...item, accountID: item.account_id }} sizeable />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const TransferPoolPalletsList: React.FC<{
  pallets?: TransferPoolPallet[];
  loading: boolean;
}> = ({ pallets, loading }) => {
  if (loading) {
    return <Spinner />;
  }

  if (!pallets?.length) {
    return null;
  }

  return (
    <>
      {pallets.map((pallet) => (
        <PalletCard key={pallet.id} pallet={pallet} />
      ))}
    </>
  );
};
